import axios from "axios";
import { API_URL, baseUrl } from "./apiConfig";
import { decodeToken } from "../utils/jwtDecode";
import store from "../Redux/store";
import { logout } from "../Redux/Auth/authSlice";
import { useNavigate } from "react-router-dom";
import {
  getToken,
  getRefreshToken,
  setTokens,
  isMobile,
} from "../utils/tokenStorage";
const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
    version: "v1",
  },
});

const unInterceptedAxiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
  },
});

const refreshAccessToken = async (): Promise<string | null> => {
  const refresh_token = await getRefreshToken();

  if (!refresh_token) {
    console.log("No refresh token available for refresh");
    return null;
  }

  try {
    const response = await axios.post(
      `${baseUrl}/${API_URL.REFRESH_TOKEN}`,
      {
        refresh_token,
      },
      {
        headers: {
          "Content-Type": "application/json",
          version: "v1",
        },
      }
    );

    const data = response?.data;

    if (data && data.access_token) {
      const newAccessToken = data?.access_token;
      const newRefreshToken = data?.refresh_token || refresh_token;

      await setTokens(newAccessToken, newRefreshToken);

      return newAccessToken;
    }
  } catch (error) {
    console.log("❌ Failed to refresh access token", error);
  }

  return null;
};

let refreshPromise: Promise<string | null> | null = null;

const getRefreshedAccessToken = async (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      return await refreshAccessToken();
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const onRequestSuccess = async (config: any) => {
  const updatedConfig = await authTokenSetting(config);

  let access_token: string | null = null;

  if (isMobile()) {
    access_token = await getToken();

    if (access_token) {
      updatedConfig.headers.Authorization = `Bearer ${access_token}`;
    }

    return updatedConfig;
  }

  access_token =
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token");

  if (access_token) {
    console.log("access_token : " + access_token);
    const decoded = decodeToken(access_token);
    console.log("decoded : " + JSON.stringify(decoded));
    const isTokenExpired = Date.now() >= decoded.exp * 1000;

    if (isTokenExpired) {
      console.log("Token Expired : " + isTokenExpired);
      console.log("🚨 Access token expired - Logging out user");
      logoutBasedOnRole(decoded);
      return updatedConfig;
    } else {
      updatedConfig.headers.Authorization = `Bearer ${access_token}`;
    }
  }

  return updatedConfig;
};

const authTokenSetting = async (config: any) => {
  if (!config?.baseURL?.startsWith(baseUrl)) {
    return config;
  }
  if (!config.headers) {
    config.headers = {};
  }
  config.headers.language = "en_US";
  return config;
};

axiosInstance.interceptors.request.use(onRequestSuccess);

axiosInstance.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    const originalRequest = error.config;
    const customResponse = error?.response;

    if (error?.response) {
      console.log(
        "axios_intercept____",
        error?.response?.status,
        JSON.stringify(error?.response?.data),
        error?.response?.data?.message
      );

      if (error?.response?.status === 401) {
        if (isMobile()) {
          if (!originalRequest._retry) {
            originalRequest._retry = true;

            const refreshedToken = await getRefreshedAccessToken();

            if (refreshedToken) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }

              originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;

              return axiosInstance(originalRequest);
            }
          }
        } else {
          console.log("🚨 401 Unauthorized Error - Logging out user");
          const access_token =
            localStorage.getItem("access_token") ||
            sessionStorage.getItem("access_token");
          if (access_token) {
            try {
              const decoded = decodeToken(access_token);
              console.log(
                "Logout triggered by 401 for user with roles:",
                decoded?.roles
              );
              logoutBasedOnRole(decoded);
            } catch (decodeError) {
              console.log(
                "Failed to decode token during 401 logout, using default logout"
              );
              log_out("/");
            }
          } else {
            console.log(
              "No access token found during 401, using default logout"
            );
            log_out("/");
          }
        }
      }

      return Promise.reject(customResponse);
    }

    return Promise.reject(error);
  }
);

export const log_out = (redirectPath: string = "/") => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  localStorage.clear();
  sessionStorage.clear();
  console.log("Logout : success");
  window.location.replace(redirectPath);
};

export const merchant_log_out = (redirectPath: string = "/merchant/login") => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  localStorage.clear();
  sessionStorage.clear();
  console.log("Logout : success");
  window.location.replace(redirectPath);
};

export const afl_log_out = (redirectPath: string = "/afl") => {
  return new Promise<void>((resolve) => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("refresh_token");
    console.log("AFL Logout : success");
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = redirectPath;
    resolve();
  });
};


const logoutBasedOnRole = (decodedToken: any) => {
  const roles = decodedToken?.roles || [];
  console.log("Roles in token:", roles);

  if (Array.isArray(roles)) {
    if (roles.includes("ROLE_AFL_USER")) {
      afl_log_out("/afl");
      return;
    }
    if(roles.includes("ROLE_MERCHANT")){
      log_out("/merchant/login")
      return;
    }
    if (roles.includes("ROLE_AAA_USER")) {
      log_out("/aaa");
      return;
    }
  }

  log_out("/"); // default fallback
};


export default axiosInstance;
